// frontend/src/DataView.js
import React, { useEffect, useState, useCallback } from "react";
import axios from 'axios';
import Modal from 'react-modal';

// Import the dedicated stylesheet for this component
import './DataView.css'; 
import FilterModal from './FilterModal.js';
import ProtectedElement from './ProtectedElement';

// Set the root element for accessibility
Modal.setAppElement('#root');

// --- Column Selection Modal ---
const ColumnSelectionModal = ({ visible, onClose, onToggleColumn, visibleColumns = [], allColumns = [] }) => {
    return (
        <Modal
            isOpen={visible}
            onRequestClose={onClose}
            contentLabel="Select Columns"
            className="dataview-modal-content column-select-modal"
            overlayClassName="modal-overlay"
            ariaHideApp={false}
        >
            <div className="modal-header">
                <h3>Select Columns to Display</h3>
            </div>
            <div className="modal-content">
                <div className="column-list">
                    {allColumns.map(col => (
                        <div key={col} className="column-item">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={visibleColumns.includes(col)}
                                    onChange={() => onToggleColumn(col)}
                                />
                                {col}
                            </label>
                        </div>
                    ))}
                </div>
            </div>
             <div className="modal-footer modal-button-group">
                <button onClick={onClose} className="modal-close-button">Close</button>
            </div>
        </Modal>
    );
};

// --- File Detail Modal ---
const FileDetailModal = ({ isOpen, onClose, fileData, allColumns = [] }) => {
    const [activeTab, setActiveTab] = useState('details');
    const [selectedKnColumn, setSelectedKnColumn] = useState('');
    const [statements, setStatements] = useState([]);
    const [knColumns, setKnColumns] = useState([]);
    const [parsingError, setParsingError] = useState('');

    useEffect(() => {
        if (fileData) {
            const availableKnCols = allColumns.filter(col => col.startsWith('kn_') && fileData[col] != null);
            setKnColumns(availableKnCols);
            setSelectedKnColumn(availableKnCols.length > 0 ? availableKnCols[0] : '');
            setActiveTab('details');
            setParsingError('');
        }
    }, [fileData, allColumns]);

    const generateStatementsFromKnData = useCallback((knDataRaw) => {
        setParsingError('');
        if (!knDataRaw) return [];
        let jsonData = null;
        try {
            jsonData = typeof knDataRaw === 'string' ? JSON.parse(knDataRaw) : knDataRaw;
        } catch (error) {
            setParsingError(`Error parsing data in '${selectedKnColumn}'. Invalid JSON.`);
            return [];
        }
        if (!jsonData) return [];
        
        const dataArray = Array.isArray(jsonData) ? jsonData : [jsonData];
        const generatedStatements = [];
        dataArray.forEach((item, index) => {
            if (item && item.node_1 && item.node_2 && item.edge) {
                generatedStatements.push({ id: `${selectedKnColumn}-${index}`, text: `${item.node_1} ${item.edge} ${item.node_2}` });
            }
        });
        return generatedStatements;
    }, [selectedKnColumn]);

    useEffect(() => {
        if (fileData && selectedKnColumn) {
            setStatements(generateStatementsFromKnData(fileData[selectedKnColumn]));
        } else {
            setStatements([]);
        }
    }, [fileData, selectedKnColumn, generateStatementsFromKnData]);

    if (!fileData) return null;

    const detailColumns = allColumns.filter(col => !col.startsWith('kn_'));

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose} contentLabel="File Details" className="dataview-modal-content file-detail-modal" overlayClassName="modal-overlay" ariaHideApp={false} >
            <div className="modal-header">
                <span>Details for: {fileData.file_name || `ID: ${fileData.id}`}</span>
                <button onClick={onClose} className="modal-close-button">Close</button>
            </div>
            <div className="modal-tabs">
                <button className={activeTab === 'details' ? 'active' : ''} onClick={() => setActiveTab('details')}>Details</button>
                {knColumns.length > 0 && ( <button className={activeTab === 'statements' ? 'active' : ''} onClick={() => setActiveTab('statements')}>Statements</button> )}
            </div>
            <div className="modal-content">
                {activeTab === 'details' && (
                    <div className="details-grid">
                        {detailColumns.map(col => (
                            <React.Fragment key={col}>
                                <div className="detail-label">{col}:</div>
                                <div className="detail-value">
                                    {typeof fileData[col] === 'object' && fileData[col] !== null ? ( <pre>{JSON.stringify(fileData[col], null, 2)}</pre> ) : ( <div>{String(fileData[col] ?? 'N/A')}</div> )}
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                )}
                {activeTab === 'statements' && (
                    <div className="statements-tab">
                        {knColumns.length > 0 ? (
                            <>
                                <div className="source-select-container">
                                    <label htmlFor="knColumnSelect">Select Source:</label>
                                    <select id="knColumnSelect" value={selectedKnColumn} onChange={(e) => setSelectedKnColumn(e.target.value)}>
                                        {knColumns.map(col => <option key={col} value={col}>{col}</option>)}
                                    </select>
                                </div>
                                {parsingError && <div className="error">{parsingError}</div>}
                                {statements.length > 0 ? (
                                    <ol className="statement-list">
                                        {statements.map((stmt, index) => <li key={stmt.id || index} className="statement-item">{stmt.text}</li>)}
                                    </ol>
                                ) : ( !parsingError && <p>No statements found for '{selectedKnColumn}'.</p> )}
                                <p className="statement-count">Displaying {statements.length} statements from: <strong>{selectedKnColumn}</strong></p>
                            </>
                        ) : ( <p>No knowledge columns (starting with 'kn_') found.</p> )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

// --- Main DataView Component ---
const DataView = ({ directories = [], handleLogout }) => {
    const [selectedDirectory, setSelectedDirectory] = useState("");
    const [allFilesData, setAllFilesData] = useState([]);
    const [allColumns, setAllColumns] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const filesPerPage = 50;
    const [totalFileCount, setTotalFileCount] = useState(0);
    const [inputValue, setInputValue] = useState("");
    const [submittedSearchTerm, setSubmittedSearchTerm] = useState("");
    const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [dataError, setDataError] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});

    const fetchViewData = useCallback(async (endpoint, params = {}, signal) => {
        setIsDataLoading(true); setDataError(null);
        try {
             const response = await axios.get(`/api${endpoint}`, { params, signal, withCredentials: true });
             console.log(`Fetched data from ${endpoint}:`, response.data);
             return response.data;
        } catch (err) {
             if (!axios.isCancel(err)) {
                  const errorMsg = err.response?.data?.error || err.message || `Failed to fetch data.`;
                  setDataError(errorMsg);
                  if (err.response?.status === 401 && handleLogout) handleLogout(false);
             }
             return null;
        } finally {
             if (!signal?.aborted) setIsDataLoading(false);
        }
    }, [handleLogout]);

    useEffect(() => {
        const controller = new AbortController();
        fetchViewData('/knowledge_table_columns', {}, controller.signal).then(data => {
            if(data && !controller.signal.aborted) {
                const columnNames = data.map(row => row[0]);
                setAllColumns(columnNames);
                const defaultVisible = ['id', 'file_name', 'directory', 'topic_title', 'key_concepts'];
                setVisibleColumns(defaultVisible.filter(c => columnNames.includes(c)));
            }
        });
        return () => controller.abort();
    }, [fetchViewData]);

    useEffect(() => {
        const controller = new AbortController();
        const offset = currentPage * filesPerPage;
        
        const isSearchingOrFiltering = submittedSearchTerm.trim() || Object.keys(appliedFilters).length > 0 || selectedDirectory;

        if (isSearchingOrFiltering) {
            const searchParams = {
                searchTerm: submittedSearchTerm.trim(),
                filters: JSON.stringify(appliedFilters),
                selectedDirectory: selectedDirectory || null,
                limit: filesPerPage,
                offset: offset,
            };
            fetchViewData('/search_files', searchParams, controller.signal).then(data => {
                if (data && !controller.signal.aborted) {
                    setAllFilesData(data.results || []);
                    setTotalFileCount(data.totalCount || 0);
                }
            });
        } else {
            const params = { limit: filesPerPage, offset };
            fetchViewData('/all_files_sorted', params, controller.signal).then(data => {
                if (data && !controller.signal.aborted) {
                    setAllFilesData(data.files || []);
                    setTotalFileCount(data.totalCount || 0);
                }
            });
        }
        return () => controller.abort();
    }, [currentPage, selectedDirectory, appliedFilters, submittedSearchTerm, fetchViewData]);

    const handleFileSelection = (file) => { setSelectedFile(file); setIsDetailModalOpen(true); };
    const handleToggleColumn = (column) => setVisibleColumns(prev => prev.includes(column) ? prev.filter(c => c !== column) : [...prev, column]);
    const handlePreviousPage = () => setCurrentPage(prev => Math.max(0, prev - 1));
    const handleNextPage = () => setCurrentPage(prev => (prev + 1) * filesPerPage < totalFileCount ? prev + 1 : prev);

    const handleSearchSubmit = (e) => { e.preventDefault(); setCurrentPage(0); setSubmittedSearchTerm(inputValue); };
    const handleDirectoryChange = (e) => { setSelectedDirectory(e.target.value); setCurrentPage(0); };
    
    const handleApplyFilters = () => {
        const newFilters = { ...tempFilters };
        Object.keys(newFilters).forEach(key => (newFilters[key] === '') && delete newFilters[key]);
        setAppliedFilters(newFilters);
        setIsFilterModalOpen(false);
        setCurrentPage(0);
    };
    
    const handleClearAllFilters = () => {
        setAppliedFilters({});
        setTempFilters({});
        setIsFilterModalOpen(false);
        setCurrentPage(0);
    };
    
    const handleClearFilterTag = (filterKey) => {
        const newFilters = { ...appliedFilters }; delete newFilters[filterKey];
        setAppliedFilters(newFilters);
        setCurrentPage(0);
    };

    return (
            <ProtectedElement
                required={["files:list-all", "files:get-directory-data", "files:read-metadata"]}
                fallbackVariant="overlay"
            >
            <div className="data-view-page">
            <h2>Data View</h2>
            <p>Explore, search, and view details of the processed knowledge files.</p>
            
            <div className="data-view-controls">
                <div className="control-group">
                    <label htmlFor="dataViewDirSelect">Filter by Directory:</label>
                    <select id="dataViewDirSelect" value={selectedDirectory} onChange={handleDirectoryChange} disabled={isDataLoading}>
                        <option value="">-- All Directories --</option>
                        {directories.map(dir => <option key={dir} value={dir}>{dir}</option>)}
                    </select>
                </div>
                <div className="search-form-wrapper">
                    <ProtectedElement required={["files:search"]} behavior="disable" disabledTooltip="Need data-view:search permission">
                        <form className="data-view-search-form" onSubmit={handleSearchSubmit}>
                            <textarea placeholder="LLM query or keywords..." value={inputValue} onChange={(e) => setInputValue(e.target.value)} disabled={isDataLoading} className="search-textarea"/>
                            <button className="search-button" type="submit" disabled={isDataLoading}>Search</button>
                        </form>
                    </ProtectedElement>
                </div>
                <div className="control-group button-group">
                        <ProtectedElement required={["files:read-metadata"]} behavior="disable" disabledTooltip="Need data-view:filters permission">
                            <button onClick={() => { setTempFilters({...appliedFilters}); setIsFilterModalOpen(true);}} disabled={isDataLoading} className="secondary">Filters ({Object.keys(appliedFilters).length})</button>
                        </ProtectedElement>
                        <ProtectedElement required={["files:read-metadata"]} behavior="disable" disabledTooltip="Need data-view:columns permission">
                            <button onClick={() => setIsColumnModalOpen(true)} disabled={isDataLoading || allColumns.length === 0} className="secondary">Columns</button>
                        </ProtectedElement>
                </div>
            </div>

            {Object.keys(appliedFilters).length > 0 && (
                <div className="applied-filters-display">
                    <strong>Active Filters:</strong>
                    {Object.entries(appliedFilters).map(([key, value]) => (
                        <span key={key} className="filter-tag">{key}: "{value}"<button onClick={() => handleClearFilterTag(key)} title={`Remove filter for ${key}`} className="filter-tag-remove">×</button></span>
                    ))}
                    <button onClick={handleClearAllFilters} className="clear-all-filters-button">Clear All</button>
                </div>
            )}

            {dataError && <div className="error">{dataError}</div>}
            {isDataLoading && <div className="loading">Loading data...</div>}
            
            {!isDataLoading && <div className="data-table-container">
                {allFilesData.length > 0 ? (
                    <table className="data-view-table">
                        <thead><tr>{visibleColumns.map(col => <th key={col}>{col}</th>)}</tr></thead>
                        <tbody>
                            {allFilesData.map((file) => (
                                <tr key={file.id} onClick={() => handleFileSelection(file)} className={selectedFile?.id === file.id ? 'selected-row' : ''} style={{ cursor: 'pointer' }}>
                                    {visibleColumns.map(col => (<td key={col} title={String(file[col] ?? '')}>{String(file[col] ?? '')}</td>))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (<p style={{ textAlign: 'center', padding: '40px' }}>No files found for the current selection.</p>)}
            </div>}

            {totalFileCount > filesPerPage && !isDataLoading && (
                <div className="pagination-controls">
                    <button onClick={handlePreviousPage} disabled={currentPage === 0}>Previous</button>
                    <span>Page {currentPage + 1} of {Math.ceil(totalFileCount / filesPerPage)}</span>
                    <button onClick={handleNextPage} disabled={(currentPage + 1) * filesPerPage >= totalFileCount}>Next</button>
                </div>
            )}

            <ProtectedElement required={["files:read-metadata"]} behavior="hide">
                <ColumnSelectionModal visible={isColumnModalOpen} onClose={() => setIsColumnModalOpen(false)} onToggleColumn={handleToggleColumn} visibleColumns={visibleColumns} allColumns={allColumns.filter(c => !c.startsWith('kn_'))} />
            </ProtectedElement>
            <ProtectedElement required={["files:read-metadata"]} behavior="hide">
                <FileDetailModal isOpen={isDetailModalOpen} onClose={() => { setSelectedFile(null); setIsDetailModalOpen(false); }} fileData={selectedFile} allColumns={allColumns} />
            </ProtectedElement>
            <ProtectedElement required={["files:read-metadata"]} behavior="hide">
                <FilterModal isOpen={isFilterModalOpen} onClose={() => setIsFilterModalOpen(false)} allColumns={allColumns.filter(c => !c.startsWith('kn_'))} currentFilters={tempFilters} onApplyFilters={handleApplyFilters} onFilterChange={(col, val) => setTempFilters(p => ({...p, [col]: val}))} onClearAllFilters={handleClearAllFilters} />
            </ProtectedElement>
        </div>
        </ProtectedElement>
    );
};

export default DataView;